import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { FEATURES } from '../constants';
import { searchSchemaOptional } from '../zod';
import { useState } from 'react';
import { useIsLoggedIn } from '../components/AuthProvider/AuthProvider';
import { authClient } from '../lib/auth-client';

export const Route = createFileRoute('/login/')({
    component: Index,
    loader: async () => {
        const session = await authClient.getSession();

        if (session.data?.user) {
            redirect({
                to: '/',
                search: searchSchemaOptional.parse({}),
                throw: true,
            });
        }
    },
});

function HuddingeLoginButton() {
    const loginWithHuddinge = async () => {
        await authClient.signIn.oauth2({
            providerId: 'huddinge',
            callbackURL: `${window.location.origin}/tool`,
        });
    };

    return (
        <button className="btn" onClick={loginWithHuddinge}>
            Login with Huddinge
        </button>
    );
}

function PasswordLoginForm() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const { recheckAuth } = useIsLoggedIn();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const result = await authClient.signIn.email({ email, password });

            if (result.error) {
                setError(result.error.message ?? 'Incorrect username or password');
            } else {
                await recheckAuth();
                navigate({ to: '/tool' });
            }
        } catch {
            setError('An error occurred. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="form-control">
                <label className="label" htmlFor="login-email">
                    <span className="label-text">Email</span>
                </label>
                <input
                    id="login-email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    className="input input-bordered"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                />
            </div>
            <div className="form-control">
                <label className="label" htmlFor="login-password">
                    <span className="label-text">Password</span>
                </label>
                <input
                    id="login-password"
                    type="password"
                    name="password"
                    autoComplete="current-password"
                    className="input input-bordered"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                />
            </div>
            {error && (
                <div className="alert alert-error">
                    <span>{error}</span>
                </div>
            )}
            <button
                type="submit"
                className="btn btn-primary w-full"
                disabled={isLoading}
            >
                {isLoading ? 'Logging in...' : 'Login'}
            </button>
        </form>
    );
}

function Index() {
    return (
        <div className="border-2 h-full grid items-center">
            <div></div>

            <div className="flex items-center justify-center h-screen bg-base-200">
                <div className="card w-96 shadow-2xl bg-base-100">
                    <div className="card-body">
                        <h2 className="card-title justify-center text-xl font-bold">
                            Login
                        </h2>
                        {FEATURES.PASSWORD_LOGIN && <PasswordLoginForm />}
                        {FEATURES.PASSWORD_LOGIN && FEATURES.HUDDINGE_LOGIN && (
                            <div className="divider">OR</div>
                        )}
                        {FEATURES.HUDDINGE_LOGIN && <HuddingeLoginButton />}
                    </div>
                </div>
            </div>
        </div>
    );
}
